import { useTranslation } from "react-i18next";
import { Button } from "@/atoms/button";
import { StatusText } from "@/atoms/status-text";
import { EmptyState } from "@/components/empty-state";
import { formatLongDate, isFuture } from "@/utils/date";
import { cadenceLine } from "@/utils/interval";
import { formatRunningMoney, fromCents } from "@/utils/money";
import { subscriptionStatus } from "@/utils/status";
import type { PortalSubscription } from "@/data/portal";

/**
 * What the client pays for (template detail 1.7, body; spec row-64-two-lines 1.6).
 *
 * One 64 row per subscription: the product with its state as a word on the first line, and the
 * money with the NEXT EVENT on the second. The next event is the message (DS18: "the state
 * always names its next event") — renews on, ends on, trial until, or the one state that costs
 * the client something today, a declined card, which is the only line here in danger text.
 *
 * The status is a word and a mark, never one of the six hand-picked hexes the old portal used,
 * and cancelled is neutral: the client decided it and the page does not editorialise.
 *
 * Cancel is a quiet tertiary at the end of the row, and it is absent on a subscription inside a
 * commitment: there the caption under the row says who to ask and when it lifts.
 *
 * The row is the 64 of the scale at every width. The commitment date is the one fact that will
 * not fit beside the other two on a phone, and it is the only fact on the line that is repeated
 * word for word in the caption under the row, so at 390 the line drops it and the caption
 * carries it. Nothing is truncated and nothing is lost.
 */

export interface SubscriptionsSectionProps {
  subscriptions: PortalSubscription[];
  coachName: string;
  supportEmail?: string;
  onCancel: (subscription: PortalSubscription) => void;
}

export function SubscriptionsSection({
  subscriptions,
  coachName,
  supportEmail,
  onCancel,
}: SubscriptionsSectionProps) {
  const { t } = useTranslation();

  if (subscriptions.length === 0) {
    return (
      <EmptyState
        heading={t("gymkeePay.portal.noSubscription", { coach: coachName })}
      />
    );
  }

  return (
    <ul className="flex flex-col">
      {subscriptions.map((subscription) => {
        const status = subscriptionStatus(subscription.status);
        const price = subscription.price;
        const amount = formatRunningMoney(
          fromCents(price.amount),
          price.currency,
        );
        const cadence = price.interval
          ? cadenceLine(t, {
              interval: price.interval,
              intervalCount: price.intervalCount ?? 1,
            })
          : "";
        const periodEnd = formatLongDate(subscription.currentPeriodEnd);
        const committed = isFuture(subscription.commitmentEndDate);
        const commitmentDate = formatLongDate(subscription.commitmentEndDate);
        const canCancel =
          !committed &&
          !subscription.cancelAtPeriodEnd &&
          (subscription.status === "active" ||
            subscription.status === "trialing");

        const danger =
          subscription.status === "past_due" ||
          subscription.status === "unpaid";

        /* The second line names the next event, which is the whole message (DS18). Written as a
           plain function of the values above it rather than a hoisted declaration: it used to be
           a `function` below the `const danger` it reads, and a hoisted function body evaluated
           at call time reached `danger` in its temporal dead zone — the portal rendered a blank
           page and the only trace was a ReferenceError in the console. */
        const secondLine = danger
          ? t("gymkeePay.portal.cardDeclinedLine", { amount, cadence })
          : subscription.cancelAtPeriodEnd && periodEnd
            ? t("gymkeePay.portal.endsOnLine", {
                amount,
                cadence,
                date: periodEnd,
              })
            : subscription.status === "trialing" && periodEnd
              ? t("gymkeePay.portal.trialUntil", {
                  amount,
                  cadence,
                  date: periodEnd,
                })
              : subscription.status === "active" && periodEnd
                ? t("gymkeePay.portal.renewsOn", {
                    amount,
                    cadence,
                    date: periodEnd,
                  })
                : t("gymkeePay.portal.plainLine", { amount, cadence });

        return (
          <li key={subscription.id} className="flex flex-col">
            <div className="ds-row ds-row-comfortable ds-hairline gap-gk-4 px-0">
              <div className="gap-gk-1 flex min-w-0 flex-1 flex-col">
                <span className="gap-gk-3 flex min-w-0 flex-wrap items-center">
                  <span className="text-[15px] font-semibold break-words text-[var(--gk-text-primary)]">
                    {subscription.productName}
                  </span>
                  {status && (
                    <StatusText
                      size={13}
                      word={t(status.key)}
                      tone={status.tone}
                    />
                  )}
                </span>
                {/* The facts line carries the money and the next event, and the row stays the 64
                    the scale gives it. The commitment is the third fact and it does not fit at
                    390: rather than truncate it or grow the row, it is dropped from the line
                    there and read from the caption underneath, which names the same date and
                    says what to do about it. At 1024 and above all three sit on the line. */}
                <span
                  className="text-[14px] break-words"
                  style={{
                    color: danger
                      ? "var(--gk-semantic-danger-text)"
                      : "var(--gk-text-secondary)",
                  }}
                >
                  {secondLine}
                  {committed && commitmentDate && (
                    <span className="hidden sm:inline">
                      {` · ${t("gymkeePay.portal.commitmentUntilDate", {
                        date: commitmentDate,
                      })}`}
                    </span>
                  )}
                </span>
              </div>
              {canCancel && (
                <Button
                  variant="tertiary"
                  className="ds-pay-hand flex-none"
                  onClick={() => onCancel(subscription)}
                >
                  {t("gymkeePay.portal.cancelAction")}
                </Button>
              )}
            </div>
            {committed && commitmentDate && (
              <p className="pt-gk-2 pb-gk-3 text-[13px] text-[var(--gk-text-tertiary)]">
                {t("gymkeePay.portal.commitmentNote", {
                  date: commitmentDate,
                  coach: coachName,
                })}
                {supportEmail && (
                  <>
                    {" "}
                    <a
                      href={`mailto:${supportEmail}`}
                      className="underline"
                      style={{ color: "var(--gk-text-secondary)" }}
                    >
                      {supportEmail}
                    </a>
                  </>
                )}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}
